import React, { useCallback } from 'react';

import { useClickOutside } from '../../hooks/useClickOutside';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useXylexaAppContext } from '../../context';
import {
  LinkGenReqBody,
  OrthancIdReqBody,
  useFindOrthancId,
  useGenerateStudyLink,
} from '../../api-client';
import { useToast } from '@xylexa/xylexa-app';
import { Icon } from '@ohif/ui';

export type shareReportModalType = {
  setShowModal: (boolean) => void;
};

type ShareFormData = {
  patientPhoneNumber: string;
  editableMessage: string;
  linkExpiry: string;
};

const getWhatsAppUrl = (phoneNumber, message) => {
  const sanitizedNumber = phoneNumber.replace(/\D/g, '');
  const encodedMessage = encodeURIComponent(message);

  return `https://wa.me/${sanitizedNumber}?text=${encodedMessage}`;
};

const expiryOptions = [
  { value: 'never', label: 'Never' },
  { value: '7 days', label: '7 days' },
  { value: '15 days', label: '15 days' },
  { value: '30 days', label: '30 days' },
  { value: '90 days', label: '90 days' },
  { value: '365 days', label: '365 days' },
];

export function ShareReportModal({ setShowModal }: shareReportModalType) {
  const { selectedStudy } = useXylexaAppContext();
  const initialMessageTemplate = '';
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<ShareFormData>({
    defaultValues: {
      patientPhoneNumber: '',
      editableMessage: initialMessageTemplate,
      linkExpiry: expiryOptions[0].value,
    },
  });

  const { modalRef: createReportModalRef } = useClickOutside(() => {
    setShowModal(false);
  });

  const { mutateAsync: findOrthancId } = useFindOrthancId();
  const { mutateAsync: generateStudyLink } = useGenerateStudyLink();
  const { showToast } = useToast();

  const handleShareSubmit: SubmitHandler<ShareFormData> = useCallback(data => {
    if (data.patientPhoneNumber.replace(/\D/g, '').length < 8) {
      showToast({ type: 'error', content: 'Please enter a valid patient phone number.' });
      return;
    }
    if (!data.editableMessage.trim()) {
      console.error('Message cannot be empty.');
      showToast({ type: 'error', content: 'Message cannot be empty.' });
      return;
    }

    const whatsappLink = getWhatsAppUrl(data.patientPhoneNumber, data.editableMessage);

    window.open(whatsappLink, '_blank');
  }, []);

  const daysStringToSeconds = (daysString: string): number => {
    const match = daysString.match(/^(\d+)\s*days$/i);
    if (match?.[1]) {
      const days = parseInt(match[1], 10);
      return days * 24 * 60 * 60;
    }

    return 0;
  };

  const createOrthancIdPayload = (studyInstanceUid: string): OrthancIdReqBody => ({
    Level: 'Study',
    Query: {
      StudyInstanceUID: studyInstanceUid,
    },
  });

  const createLinkGenPayload = (
    orthancId: string,
    studyInstanceUid: string,
    linkExpiry: string
  ): LinkGenReqBody => ({
    Resources: [
      {
        OrthancId: orthancId,
        DicomUid: studyInstanceUid,
        Level: 'study',
      },
    ],
    Type: 'ohif-viewer-publication',
    ValidityDuration: daysStringToSeconds(linkExpiry),
  });

  const generateMessageTemplate = (newReportUrl: string, linkExpiry: string): string => {
    const radiologistName = '';
    return `Hello! Your medical study report from Diagnostic Imaging is now available for review (read-only mode).

Access Link: ${newReportUrl}
(Link will expire in: ${linkExpiry})

If you have any questions, please contact our office.

Best regards,
${radiologistName}`;
  };

  const handleGenerateLink = useCallback(
    async (data: ShareFormData) => {
      const studyInstanceUid = selectedStudy?.studyInstanceUid;

      if (!data.linkExpiry) {
        console.error('Validation Error: Please select an expiry period.');
        return;
      }

      if (!studyInstanceUid) {
        console.error('Validation Error: Study instance UID is missing.');
        return;
      }

      const genericErrorMessage = 'Failed to generate link. Please try again.';

      try {
        const orthancIdPayload = createOrthancIdPayload(studyInstanceUid);

        const orthancRes = await findOrthancId(orthancIdPayload);

        const orthancId = orthancRes.data?.[0];

        if (!orthancId) {
          showToast({ type: 'error', content: 'Orthanc ID not found for this study.' });
          return;
        }

        const linkGenPayload = createLinkGenPayload(orthancId, studyInstanceUid, data.linkExpiry);

        const linkRes = await generateStudyLink(linkGenPayload);

        const newReportUrl = linkRes?.data?.Url;

        if (!newReportUrl) {
          showToast({ type: 'error', content: 'Link URL not returned by the server.' });
          return;
        }

        const generatedMessage = generateMessageTemplate(newReportUrl, data.linkExpiry);

        setValue('editableMessage', generatedMessage, { shouldDirty: true });

        return {
          url: newReportUrl,
          message: generatedMessage,
        };
      } catch (error) {
        showToast({ type: 'error', content: genericErrorMessage });
      }
    },

    [selectedStudy, findOrthancId, generateStudyLink, setValue, showToast]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden outline-none focus:bg-transparent focus:outline-none"
      style={{ backdropFilter: 'blur(5px)' }}
    >
      <div className="relative my-6 mx-auto w-1/2 max-w-3xl">
        <div
          ref={createReportModalRef}
          className="bg-secondary-light relative flex w-full flex-col rounded-lg border-0 shadow-lg outline-none focus:outline-none"
        >
          <div className="border-blueGray-200 flex items-start justify-between rounded-t border-solid p-5">
            <h4 className="text-3xl font-semibold text-white">Share Study</h4>
            <button
              className="float-right ml-auto border-0 bg-transparent p-1 text-3xl font-semibold leading-none text-black opacity-5 outline-none focus:outline-none"
              onClick={() => setShowModal(false)}
            >
              <span className="block h-6 w-6 bg-transparent text-2xl text-white opacity-5 outline-none focus:outline-none">
                ×
              </span>
            </button>
          </div>
          {/*body*/}
          <div className="flex min-h-screen flex-col">
            <div className="flex items-center justify-center">
              <div
                className="bg-whitetransition-all w-full max-w-md transform duration-300"
                onClick={e => e.stopPropagation()}
              >
                <label
                  htmlFor="phone"
                  className="mb-2 mt-4 block text-lg font-medium text-white"
                >
                  Patient Mobile Number (e.g., 923551234567)
                </label>
                <div className="relative mb-6">
                  <Icon
                    className="absolute top-9 left-2 mt-1"
                    name="phone-icon"
                  />
                  <input
                    style={{ backgroundColor: '#2A2A2A' }}
                    id="phone"
                    type="tel"
                    placeholder="Include country code"
                    className="w-full rounded-md py-2 pl-10 pr-4 text-white transition"
                    {...register('patientPhoneNumber')}
                  />
                </div>

                <label
                  htmlFor="linkExpiry"
                  className="mb-2 block text-lg font-medium text-white"
                >
                  Link Expiry
                </label>
                <div className="relative mb-6">
                  <select
                    id="linkExpiry"
                    style={{ backgroundColor: '#2A2A2A' }}
                    className="w-full appearance-none rounded-md py-2 px-4 text-white transition"
                    {...register('linkExpiry')}
                  >
                    {expiryOptions.map(option => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="ml-2 bg-[#2A2A2A]"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <Icon
                    className="absolute ml-2 mr-2"
                    name="arrow-down"
                  />
                </div>

                <div className="mb-2 flex justify-end">
                  <button
                    className="rounded bg-blue-600 px-4 py-2 text-sm font-bold uppercase text-white outline-none transition-all duration-150 ease-linear hover:bg-black hover:bg-blue-700 focus:outline-none disabled:opacity-50"
                    type="button"
                    onClick={handleSubmit(handleGenerateLink)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Generating...' : 'Generate Link'}
                  </button>
                </div>

                <label
                  htmlFor="message"
                  className="mb-2 block text-lg font-medium text-white"
                >
                  Message
                </label>
                <div className="relative mb-6">
                  <textarea
                    id="message"
                    style={{ backgroundColor: '#2A2A2A' }}
                    rows={6}
                    placeholder="Enter your custom message here..."
                    className="w-full resize-none rounded-md p-3 text-sm text-white transition"
                    {...register('editableMessage')}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-blueGray-200 flex items-center justify-end border-solid p-6">
            <button
              className="background-transparent mr-1 mb-1 rounded px-6 py-2 text-sm font-bold uppercase text-red-500 outline-none transition-all duration-150 ease-linear hover:bg-black hover:text-white focus:outline-none"
              type="button"
              style={{ color: '#ef4444' }}
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button
              className="background-transparent mr-1 mb-1 rounded px-6 py-2 text-sm font-bold uppercase text-white outline-none transition-all duration-150 ease-linear hover:bg-black focus:outline-none disabled:opacity-50"
              type="button"
              onClick={handleSubmit(handleShareSubmit)}
              disabled={isSubmitting}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareReportModal;
